import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from './shared/Modal';
import { addMCPServer } from '../services/api';

const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--fg-primary)',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
};

const labelStyle = {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--muted)',
    marginBottom: '0.35rem',
};

const fieldStyle = { marginBottom: '1rem' };

const MCPAddModal = ({ onClose, onAdded, userEmail }) => {
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [description, setDescription] = useState('');
    const [working, setWorking] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const validate = () => {
        if (!name.trim()) return 'Name is required';
        if (!url.trim()) return 'MCP server URL is required';
        try { new URL(url.trim()); } catch { return 'URL must be a valid URL (e.g. https://mcp.example.com/v1/mcp)'; }
        return null;
    };

    const handleSubmit = async () => {
        const err = validate();
        if (err) { setError(err); return; }
        setError('');
        setWorking(true);
        const result = await addMCPServer(
            { name: name.trim(), url: url.trim(), apiKey: apiKey.trim(), description: description.trim() },
            userEmail
        );
        setWorking(false);
        if (!result.success) {
            setError(result.error || 'Failed to add MCP server');
            return;
        }
        setDone(true);
        if (onAdded) onAdded();
    };

    if (done) {
        return (
            <Modal isOpen onClose={onClose} title="MCP Server Added" maxWidth="480px">
                <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', color: 'var(--fg-primary)', marginBottom: '0.5rem', fontWeight: 600 }}>
                        {name} added successfully
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
                        OpenWebUI will pick up the new MCP server on its next tool sync.
                    </div>
                    <button className="btn btn-primary" onClick={onClose}>Done</button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen onClose={onClose} title="Add MCP Server" maxWidth="520px"
            footer={
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={onClose} disabled={working}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={working}>
                        {working ? 'Adding...' : 'Add MCP Server'}
                    </button>
                </div>
            }
        >
            <div style={{ padding: '0.25rem 0' }}>
                {error && (
                    <div style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical)', borderRadius: 6, padding: '0.6rem 0.85rem', marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--critical)' }}>
                        {error}
                    </div>
                )}

                <div style={fieldStyle}>
                    <label style={labelStyle}>Server Name *</label>
                    <input
                        style={inputStyle}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Atlassian Jira"
                        autoFocus
                    />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>MCP Server URL *</label>
                    <input
                        style={inputStyle}
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                        placeholder="e.g. https://mcp.atlassian.com/v1/mcp"
                    />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>API Key / Bearer Token</label>
                    <input
                        style={inputStyle}
                        type="password"
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        placeholder="Leave blank if not required"
                    />
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                        Sent as x-litellm-api-key header if provided
                    </div>
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Description</label>
                    <input
                        style={inputStyle}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Optional — shown in OpenWebUI tool list"
                    />
                </div>

                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                    The MCP server will be registered in OpenWebUI with public read access for all users.
                    The connection will route through the LiteLLM gateway.
                </div>
            </div>
        </Modal>
    );
};

MCPAddModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onAdded: PropTypes.func,
    userEmail: PropTypes.string,
};

export default MCPAddModal;
